import type {ChangeSpec} from '@codemirror/state'
import type {EditorView} from '@codemirror/view'
import type {Root} from 'mdast'

import {MarkdownLine} from '../../../language-tools/markdown/markdown-line'
import {containsRange, getNodesOverlappingRange} from '../../../language-tools/syntax-tree/node-range-utils'
import {isPositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {getMarkdownTree} from '../../extensions/markdown-parser'

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

const getHeadingNode = (tree: Root, start: number, end: number) =>
  [...getNodesOverlappingRange(tree, start, end)].find(node => node.type === 'heading')

export const formatHeading = (view: EditorView, level: HeadingLevel) => {
  if (!view.hasFocus) view.focus()

  const tree = getMarkdownTree(view.state)
  const source = view.state.doc.toString()
  const selection = view.state.selection.main
  const selectedLines = MarkdownLine.linesAtRange(tree, source, selection.from, selection.to)
  const atxPrefix = `${'#'.repeat(level)} `

  // Check if all selected lines are already at the target heading level
  const headingNodes = selectedLines.map(line => getHeadingNode(tree, line.start, line.end))
  const shouldAddLevel = !(headingNodes.length > 0 && headingNodes.every(node => node?.depth === level))

  const changes: ChangeSpec[] = []
  let changeSelection = undefined

  for (const line of selectedLines) {
    const headingNode = getHeadingNode(tree, line.start, line.end)
    let headingDelimiter = null

    // Remove existing heading formatting
    if (headingNode) {
      for (const child of headingNode.children ?? []) {
        if (!isPositionedNode(child) || child.type !== 'delimiter') continue

        const isATXDelimiter = child.tokenType === 'atxHeadingSequence'
        const isSetextDelimiter = child.tokenType === 'setextHeadingLine'
        if (!isATXDelimiter && !isSetextDelimiter) continue

        headingDelimiter = child
        if (isATXDelimiter) {
          // Remove ATX heading prefix (include trailing whitespace)
          changes.push({
            from: child.position.start.offset,
            to: child.position.end.offset + 1,
          })
        } else {
          // Remove setext heading - remove the entire underline
          const setextLine = line.next()
          if (setextLine) {
            changes.push({
              from: setextLine.start,
              to: setextLine.end,
            })
          }
        }
        break
      }
    }

    // Add new heading formatting if needed
    if (shouldAddLevel) {
      changes.push({
        from: line.start,
        insert: atxPrefix,
      })

      const withinHeadingDelimiter =
        headingDelimiter &&
        isPositionedNode(headingDelimiter) &&
        containsRange(
          selection.from,
          selection.to,
          headingDelimiter.position.start.offset,
          headingDelimiter.position.end.offset,
          true,
        )
      // Handle caret position when selection is at line start or within heading delimiter
      if (selection.empty && (selection.from === line.start || withinHeadingDelimiter)) {
        changeSelection = {anchor: line.start + atxPrefix.length}
      }
    }
  }

  view.dispatch({
    changes,
    selection: changeSelection,
    userEvent: 'input',
    scrollIntoView: true,
  })

  return true
}
